import { useSearchParams } from 'next/navigation';
import { InfiniteData, useInfiniteQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseQueryCampaignsHotelsWithOffersData =
  Offers.Endpoints.CampaignHotelsWithOffers;

interface UseQueryCampaignsHotelsWithOffersOptions {
  initialData?: InfiniteData<UseQueryCampaignsHotelsWithOffersData>;
}

interface UseQueryCampaignsHotelsWithOffersParameters {
  brand?: Content.Properties.Brand;
  campaignId: string;
}

const useQueryCampaignsHotelsWithOffers = (
  parameters: UseQueryCampaignsHotelsWithOffersParameters,
  options?: UseQueryCampaignsHotelsWithOffersOptions,
) => {
  const locale = getLocale();
  const searchParams = useSearchParams();

  const query = useInfiniteQuery<UseQueryCampaignsHotelsWithOffersData>(
    [
      QueryId.CAMPAIGNS_HOTELS_WITH_OFFERS,
      parameters,
      locale,
      searchParams?.toString() ?? '',
    ],
    async ({ pageParam = 0 }) => {
      const baseUrl = '/api/campaigns/hotels-with-offers';
      const newParameters = new URLSearchParams({
        batchSize: String(searchParams?.get('batchSize')),
        campaignId: parameters.campaignId as string,
        skip: pageParam.toString(),
        sortBy: String(searchParams?.get('sortBy')),
      });

      if (parameters.brand) {
        newParameters.append('brand', String(parameters.brand));
      }

      if (searchParams?.get('adultsNumber')) {
        newParameters.append(
          'adultsNumber',
          searchParams.get('adultsNumber') === '0'
            ? ''
            : String(searchParams.get('adultsNumber')),
        );
      }

      if (searchParams?.get('adultsPerRoom')) {
        newParameters.append(
          'adultsPerRoom',
          String(searchParams.get('adultsPerRoom')),
        );
      }

      if (searchParams?.get('childrenAges')) {
        newParameters.append(
          'childrenAges',
          String(searchParams.get('childrenAges')),
        );
      }

      if (searchParams?.get('childrenAgesPerRoom')) {
        newParameters.append(
          'childrenAgesPerRoom',
          String(searchParams.get('childrenAgesPerRoom')),
        );
      }

      if (searchParams?.get('departureDate')) {
        newParameters.append(
          'departureDate',
          String(searchParams.get('departureDate')),
        );
      }

      if (searchParams?.get('departureIds')) {
        newParameters.append(
          'departureIds',
          String(searchParams.get('departureIds')),
        );
      }

      if (searchParams?.get('destinationIds')) {
        newParameters.append(
          'destinationIds',
          String(searchParams.get('destinationIds')),
        );
      }

      if (searchParams?.get('destinationRegionIds')) {
        newParameters.append(
          'destinationRegionIds',
          String(searchParams.get('destinationRegionIds')),
        );
      }

      if (searchParams?.get('durationType')) {
        newParameters.append(
          'durationType',
          String(searchParams.get('durationType')),
        );
      }

      if (searchParams?.get('filterBoardTypes')) {
        newParameters.append(
          'filterBoardTypes',
          String(searchParams.get('filterBoardTypes')),
        );
      }

      if (searchParams?.get('filterCharter')) {
        newParameters.append(
          'filterCharter',
          String(searchParams.get('filterCharter')),
        );
      }

      if (searchParams?.get('filterDirect')) {
        newParameters.append(
          'filterDirect',
          String(searchParams.get('filterDirect')),
        );
      }

      if (searchParams?.get('filterDiscount')) {
        newParameters.append(
          'filterDiscount',
          String(searchParams.get('filterDiscount')),
        );
      }

      if (searchParams?.get('filterFacilityIds')) {
        newParameters.append(
          'filterFacilityIds',
          String(searchParams.get('filterFacilityIds')),
        );
      }

      if (searchParams?.get('filterFacilityValues')) {
        newParameters.append(
          'filterFacilityValues',
          String(searchParams.get('filterFacilityValues')),
        );
      }

      if (searchParams?.get('filterPricePerPerson')) {
        newParameters.append(
          'filterPricePerPerson',
          String(searchParams.get('filterPricePerPerson')),
        );
      }

      if (searchParams?.get('filterStars')) {
        newParameters.append(
          'filterStars',
          String(searchParams.get('filterStars')),
        );
      }

      if (searchParams?.get('filterTransportHotel')) {
        newParameters.append(
          'filterTransportHotel',
          String(searchParams.get('filterTransportHotel')),
        );
      }

      if (searchParams?.get('hotelIds')) {
        newParameters.append('hotelIds', String(searchParams.get('hotelIds')));
      }

      if (searchParams?.get('maxDepartureDate')) {
        newParameters.append(
          'maxDepartureDate',
          String(searchParams.get('maxDepartureDate')),
        );
      }

      const fetchResponse = await fetch(
        `${baseUrl}?${newParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useQueryCampaignsHotelsWithOffers', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled: !!searchParams?.get('durationType'),
      getNextPageParam: lastPage => {
        const { pagination } = lastPage;
        const totalResults = pagination.count;
        const currentSkip = pagination.skip;
        const currentTake = pagination.take;

        const nextSkip = currentSkip + currentTake;

        if (nextSkip < totalResults) {
          return nextSkip;
        }

        return undefined;
      },
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};

export default useQueryCampaignsHotelsWithOffers;
